import { DOCUMENT, Inject, Injectable } from '@angular/core';
import {
  LocalStorageService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import { fromEvent, merge, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

/**
 * Reads the user settings for the Customer Number: "show in the top bar" (default off) and
 * "show in the user menu" (default on).
 * The user settings plugin (`etrusted-user-settings`) writes the setting to its own
 * scoped local storage and dispatches {@link CustomerNumberSettingService.CHANGED_EVENT}
 * on the window. The `storage` event covers a change from another tab.
 */
@Injectable()
export class CustomerNumberSettingService {
  public static readonly PLUGIN_SCOPE = 'etrusted-user-settings';
  public static readonly KEY = 'showCustomerNumberInTopBar';
  public static readonly MENU_KEY = 'showCustomerNumberInMenu';
  public static readonly CHANGED_EVENT =
    'tswp:user-settings:customer-number-display-changed';

  public readonly showInTopBar$: Observable<boolean>;
  public readonly showInMenu$: Observable<boolean>;

  public constructor(
    @Inject(COMMON_TOKENS.LocalStorageService)
    private readonly _localStorageService: LocalStorageService,
    @Inject(DOCUMENT) document: Document
  ) {
    const win = document.defaultView;
    const changed$ = merge(
      fromEvent(win, CustomerNumberSettingService.CHANGED_EVENT),
      fromEvent(win, 'storage')
    ).pipe(startWith(null));

    this.showInTopBar$ = changed$.pipe(
      map(() => this.read(CustomerNumberSettingService.KEY, false)),
      distinctUntilChanged()
    );
    this.showInMenu$ = changed$.pipe(
      map(() => this.read(CustomerNumberSettingService.MENU_KEY, true)),
      distinctUntilChanged()
    );
  }

  private read(key: string, defaultValue: boolean): boolean {
    try {
      const stored = this._localStorageService
        .createScoped(CustomerNumberSettingService.PLUGIN_SCOPE)
        .select<unknown>(key);
      return typeof stored === 'boolean' ? stored : defaultValue;
    } catch {
      return defaultValue;
    }
  }
}
